import { createFileRoute, redirect } from "@tanstack/react-router";
import HeadManager from "../services/HeadManager";
import { useEffect, useState } from "react";
import { trackCustomEvent } from "../services/analytics";
import { formatToCheckinDate, getDefaultCheckinDate } from "../services/BookingConstants";
import Modal from "../components/modal/Modal";
import SilvestreContactForm from "../components/custom/SilvestreContactForm";

export const Route = createFileRoute("/rates")({
  beforeLoad: ({ search }) => {
    const s = search as Record<string, any>;
    const rawCheckin = s?.checkin;
    const checkin = rawCheckin ? formatToCheckinDate(rawCheckin) : getDefaultCheckinDate();
    const nights = s?.nights ?? 2;
    const currency = s?.currency ?? "USD";

    const needsRedirect = !rawCheckin || rawCheckin !== checkin || !s?.currency;

    if (needsRedirect) {
      const newSearch = {
        ...s,
        currency,
        checkin,
        nights,
      };

      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("currency", currency);
        urlParams.set("checkin", checkin);
        if (!urlParams.has("nights")) urlParams.set("nights", String(nights));
        window.location.replace(`/rates?${urlParams.toString()}`);
      }

      throw redirect({
        to: "/rates",
        search: newSearch,
        replace: true,
      });
    }
  },
  head: (ctx) => {
    return HeadManager.routerHead(ctx);
  },
  component: RouteComponent,
  staticData: {
    booking_engine: {
      show_rates: true,
    },
  },
});

function isMinStayCode(val: any): boolean {
  if (!val || typeof val !== "string") return false;
  const upper = val.toUpperCase().trim();
  return (
    upper.includes("MIN_STAY") ||
    upper.includes("MINIMUM_STAY") ||
    upper.includes("MIN_NIGHT") ||
    upper.includes("MINIMUM_NIGHT") ||
    upper.includes("MIN_LOS") ||
    upper.includes("MINIMUM_LOS") ||
    upper.includes("STAY_TOO_SHORT") ||
    (upper.includes("MIN") && (upper.includes("STAY") || upper.includes("NIGHT"))) ||
    upper.includes("RESTRICTION")
  );
}

function parseDateComponents(dateStr?: string): { year: number; month: number; day: number } | null {
  if (!dateStr || typeof dateStr !== "string") return null;
  const trimmed = dateStr.trim();
  if (trimmed.includes("-")) {
    const parts = trimmed.split("-").map(Number);
    if (parts.length === 3 && parts.every((p) => !isNaN(p))) {
      if (parts[0] > 1000) {
        return { year: parts[0], month: parts[1], day: parts[2] };
      }
      return { day: parts[0], month: parts[1], year: parts[2] };
    }
  } else if (trimmed.includes("/")) {
    const parts = trimmed.split("/").map(Number);
    if (parts.length === 3 && parts.every((p) => !isNaN(p))) {
      return { day: parts[0], month: parts[1], year: parts[2] };
    }
  }
  const d = new Date(trimmed);
  if (!isNaN(d.getTime())) {
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }
  return null;
}

function isMinimumNightsRequirementNotSatisfied(
  res: any,
  calculatedNights?: number,
  startDate?: string,
  endDate?: string
): boolean {
  if (!res || typeof res !== "object") return false;
  const nights = calculatedNights;

  // 1. Check direct min-stay reason codes or causes
  if (isMinStayCode(res.cause) || isMinStayCode(res.noAvailableReason)) return true;
  if (isMinStayCode(res.tracking?.cause) || isMinStayCode(res.tracking?.noAvailableReason)) return true;

  if (Array.isArray(res.tracking?.hotelInfoBAR)) {
    if (
      res.tracking.hotelInfoBAR.some(
        (h: any) => isMinStayCode(h?.noAvailableReason) || isMinStayCode(h?.cause)
      )
    ) {
      return true;
    }
  }

  // 2. Check suggestions description or metrics
  const suggestions = res.suggestions || {};
  if (typeof suggestions.description === "string") {
    const desc = suggestions.description;
    if (
      /min(imum)?[\s_-]*(stay|night|noches|estancia)/i.test(desc) ||
      /m[ií]nimo/i.test(desc)
    ) {
      return true;
    }
  }
  if (typeof suggestions.minStay === "number" && nights !== undefined && nights < suggestions.minStay) return true;
  if (typeof suggestions.minNights === "number" && nights !== undefined && nights < suggestions.minNights) return true;
  if (typeof suggestions.rangeMinDays === "number" && nights !== undefined && nights < suggestions.rangeMinDays) return true;

  // 3. Check notifications
  const notifications = Array.isArray(res.notifications) ? res.notifications : [];
  for (const n of notifications) {
    if (typeof n === "string") {
      if (isMinStayCode(n) || /min(imum)?[\s_-]*(stay|night|noches|estancia)/i.test(n)) {
        return true;
      }
    } else if (n && typeof n === "object") {
      if (
        isMinStayCode(n.code) ||
        isMinStayCode(n.type) ||
        isMinStayCode(n.id) ||
        isMinStayCode(n.reason) ||
        isMinStayCode(n.message) ||
        isMinStayCode(n.text) ||
        n.id === "core.label.min_nights_required" ||
        n.id === "core.label.min_nights" ||
        /min(imum)?[\s_-]*(stay|night|noches|estancia)/i.test(
          String(n.message || n.text || n.description || "")
        )
      ) {
        return true;
      }
    }
  }

  // 4. Check minStayDates in calendar or unavailability
  const calendar = res.calendar || res.finder?.calendar || res.unavailability || {};
  const minStayDates =
    calendar.minStayDates ||
    calendar.unavailability?.minStayDates ||
    res.minStayDates ||
    res.unavailability?.minStayDates;

  if (minStayDates && typeof minStayDates === "object") {
    if (nights !== undefined && nights > 0) {
      const startParsed = parseDateComponents(startDate);
      if (startParsed) {
        const daysToCheck = Math.max(1, nights);
        for (let i = 0; i < daysToCheck; i++) {
          const d = new Date(startParsed.year, startParsed.month - 1, startParsed.day + i);
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yyyy = String(d.getFullYear());
          const dmy = `${dd}/${mm}/${yyyy}`;
          const ymd = `${yyyy}-${mm}-${dd}`;
          const req = minStayDates[dmy] ?? minStayDates[ymd];
          if (typeof req === "number" && req > 0 && nights < req) {
            return true;
          }
        }
      } else {
        for (const req of Object.values(minStayDates)) {
          if (typeof req === "number" && req > 0 && nights < req) {
            return true;
          }
        }
      }
    }
  }

  // 5. Check calendar rangeMinDays or minNights
  if (typeof calendar.minNights === "number" && nights !== undefined && nights < calendar.minNights) return true;
  if (typeof calendar.rangeMinDays === "number" && calendar.rangeMinDays > 1 && nights !== undefined && nights < calendar.rangeMinDays) return true;
  if (typeof res.minNights === "number" && nights !== undefined && nights < res.minNights) return true;
  if (typeof res.rangeMinDays === "number" && res.rangeMinDays > 1 && nights !== undefined && nights < res.rangeMinDays) return true;

  // 6. Check items/rooms restrictions
  const items = res.items || [];
  if (Array.isArray(items) && items.length > 0) {
    const allRoomsBlockedByMinStay = items.every((room: any) => {
      if (typeof room.minStay === "number" && nights !== undefined && nights < room.minStay) {
        return true;
      }
      if (isMinStayCode(room.noAvailableReason) || isMinStayCode(room.restriction)) {
        return true;
      }
      if (Array.isArray(room.restrictions)) {
        const hasMinStayRest = room.restrictions.some(
          (r: any) =>
            isMinStayCode(r?.type) ||
            isMinStayCode(r?.code) ||
            isMinStayCode(r?.value) ||
            /min(imum)?[\s_-]*(stay|night)/i.test(String(r?.value || r?.message || ""))
        );
        if (hasMinStayRest) return true;
      }
      if (Array.isArray(room.boards)) {
        const allBoardsHaveMinStay = room.boards.every((b: any) => {
          if (typeof b.minStay === "number" && nights !== undefined && nights < b.minStay) {
            return true;
          }
          if (Array.isArray(b.restrictions)) {
            return b.restrictions.some(
              (r: any) =>
                isMinStayCode(r?.type) ||
                isMinStayCode(r?.code) ||
                isMinStayCode(r?.value) ||
                /min(imum)?[\s_-]*(stay|night)/i.test(String(r?.value || r?.message || ""))
            );
          }
          return false;
        });
        if (room.boards.length > 0 && allBoardsHaveMinStay) return true;
      }
      return false;
    });
    if (allRoomsBlockedByMinStay) {
      return true;
    }
  }

  // 7. Check error message / status
  if (typeof res.message === "string" && /min(imum)?[\s_-]*(stay|night|noches|estancia)/i.test(res.message)) return true;
  if (typeof res.error === "string" && /min(imum)?[\s_-]*(stay|night|noches|estancia)/i.test(res.error)) return true;

  return false;
}

function RouteComponent() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [searchDetails, setSearchDetails] = useState<{
    startDate?: string;
    endDate?: string;
    personsCount?: number;
    adultsCount?: number;
    childrenCount?: number;
    nights?: number;
  }>({});

  useEffect(() => {
    trackCustomEvent("rates_view", { value: 0.1, currency: "USD" });

    let ratesResponseCount = 0;

    const handleRatesResponse = (data: any) => {
      try {
        console.log("ratesResponse triggered, count:", ratesResponseCount + 1);
        ratesResponseCount++;
        if (ratesResponseCount <= 1) {
          console.log("Ignoring first ratesResponse placeholder event.");
          return;
        }

        const res = data?.response || {};
        const finder = res.finder || {};
        const items = res.items || [];

        // 1. Start and end dates
        const formatCalendarDate = (dateVal: any): string => {
          if (!dateVal) return "";
          if (typeof dateVal.toISOString === "function") {
            return dateVal.toISOString().split("T")[0];
          }
          if (typeof dateVal === "string") {
            return dateVal.split("T")[0];
          }
          try {
            const d = new Date(dateVal);
            if (!isNaN(d.getTime())) {
              return d.toISOString().split("T")[0];
            }
          } catch (e) { }
          return String(dateVal);
        };

        const startDate = formatCalendarDate(finder.calendar?.[0]);
        const endDate = formatCalendarDate(finder.calendar?.[1]);

        // 2. Persons count and occupancy breakdown
        let personsCount = 0;
        let adultsCount = 0;
        let childrenCount = 0;
        const childrenAges: number[] = [];
        let roomsCount = 0;

        if (finder.occupation && Array.isArray(finder.occupation)) {
          roomsCount = finder.occupation.length;
          finder.occupation.forEach((roomOccupants: any) => {
            if (Array.isArray(roomOccupants)) {
              roomOccupants.forEach((occupant: any) => {
                if (occupant && typeof occupant.amount === "number") {
                  personsCount += occupant.amount;
                  if (occupant.type === 1) {
                    adultsCount += occupant.amount;
                  } else {
                    childrenCount += occupant.amount;
                    if (Array.isArray(occupant.ages)) {
                      childrenAges.push(...occupant.ages.filter((age: any) => typeof age === "number" && age < 99));
                    }
                  }
                }
              });
            }
          });
        }

        let calculatedNights: number | undefined;
        if (startDate && endDate) {
          try {
            const startD = new Date(startDate);
            const endD = new Date(endDate);
            const diffTime = endD.getTime() - startD.getTime();
            if (!isNaN(diffTime) && diffTime > 0) {
              calculatedNights = Math.round(diffTime / (1000 * 60 * 60 * 24));
            }
          } catch (e) { }
        }

        setSearchDetails({
          startDate,
          endDate,
          personsCount,
          adultsCount,
          childrenCount,
          nights: calculatedNights,
        });

        // 3. Room availability and counts
        const totalRoomsCount = items.length;
        const availableItems = items.filter((item: any) => !item.soldOut);
        const isAvailable = availableItems.length > 0;
        const availableRoomsCount = availableItems.length;
        const soldOutRoomsCount = totalRoomsCount - availableRoomsCount;
        const availableRoomNames = availableItems
          .map((item: any) => item.name || item.title)
          .filter(Boolean);

        // 4. Cost metrics across available boards
        let totalDayPrice = 0;
        let boardCount = 0;
        let minDayPrice: number | undefined;
        let maxDayPrice: number | undefined;

        availableItems.forEach((room: any) => {
          if (Array.isArray(room.boards)) {
            room.boards.forEach((board: any) => {
              if (typeof board.dayPrice === "number" && board.dayPrice > 0) {
                totalDayPrice += board.dayPrice;
                boardCount++;
                if (minDayPrice === undefined || board.dayPrice < minDayPrice) {
                  minDayPrice = board.dayPrice;
                }
                if (maxDayPrice === undefined || board.dayPrice > maxDayPrice) {
                  maxDayPrice = board.dayPrice;
                }
              }
            });
          }
        });
        const averageCostPerNight = boardCount > 0 ? totalDayPrice / boardCount : 0;

        // 5. Mirai tracking metadata & diagnostics
        const tracking = res.tracking || res.data?.tracking || {};
        const miraiStatus =
          tracking.status ||
          (isAvailable ? "AVAILABILITY" : "NO_AVAILABILITY");
        const userCountry =
          tracking.userLocation?.country?.iso ||
          tracking.country ||
          res.country ||
          undefined;
        const notifications = Array.isArray(res.notifications) ? res.notifications : [];

        const isMinNightsNotSatisfied = isMinimumNightsRequirementNotSatisfied(
          res,
          calculatedNights,
          startDate,
          endDate
        );

        const ratesSearchPayload = {
          startDate,
          endDate,
          nights: calculatedNights,
          personsCount,
          adultsCount,
          childrenCount,
          childrenAges,
          roomsCount,
          isAvailable,
          isMinNightsNotSatisfied,
          availableRoomsCount,
          soldOutRoomsCount,
          totalRoomsCount,
          availableRoomNames,
          averageCostPerNight,
          minCostPerNight: minDayPrice,
          maxCostPerNight: maxDayPrice,
          currency: res.currency || "USD",
          miraiStatus,
          userCountry,
          notificationsCount: notifications.length,
        };

        console.log("ratesResponse received and processed:", ratesSearchPayload);

        trackCustomEvent("rates_search", ratesSearchPayload);

        if (isAvailable === false && !isMinNightsNotSatisfied) {
          setIsContactModalOpen(true);
        } else {
          setIsContactModalOpen(false);
        }
      } catch (err) {
        console.error("Error processing ratesResponse event:", err);
      }
    };

    let subscribed = false;
    let intervalId: any;

    const trySubscribe = () => {
      const mirai = (window as any).Mirai;
      if (mirai && mirai.Event && typeof mirai.Event.subscribe === "function") {
        console.log("Subscribing to Mirai ratesResponse event...");
        mirai.Event.subscribe("ratesResponse", handleRatesResponse);
        subscribed = true;
        if (intervalId) clearInterval(intervalId);
      }
    };

    if (typeof window !== "undefined") {
      trySubscribe();
      if (!subscribed) {
        intervalId = setInterval(trySubscribe, 100);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (subscribed && typeof window !== "undefined") {
        const mirai = (window as any).Mirai;
        if (mirai && mirai.Event && typeof mirai.Event.unsubscribe === "function") {
          console.log("Unsubscribing from Mirai ratesResponse event.");
          mirai.Event.unsubscribe("ratesResponse", handleRatesResponse);
        }
      }
    };
  }, []);

  const searchDetailsSummary = [
    searchDetails.startDate && searchDetails.endDate
      ? `Dates: ${searchDetails.startDate} to ${searchDetails.endDate}`
      : "",
    searchDetails.nights !== undefined ? `Nights: ${searchDetails.nights}` : "",
    searchDetails.adultsCount !== undefined || searchDetails.childrenCount !== undefined
      ? `Guests: ${searchDetails.adultsCount || 0} adults${searchDetails.childrenCount ? `, ${searchDetails.childrenCount} children` : ""}`
      : searchDetails.personsCount !== undefined
        ? `Guests: ${searchDetails.personsCount}`
        : "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Modal
      opened={isContactModalOpen}
      fullscreen={false}
      onClose={() => setIsContactModalOpen(false)}
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsContactModalOpen(false)}
          className="absolute top-3 right-3 p-2 z-10 cursor-pointer focus:outline-none"
          aria-label="Close"
        >
          <i className="icon-close block"></i>
        </button>
        {isContactModalOpen && (
          <SilvestreContactForm
            title="We have one remaining option"
            description="Please note that this option is not available to book online and requires communication with our team. Please leave your details below and we will get in touch with you ASAP"
            appendQueryParams={true}
            appendMessage={searchDetailsSummary}
            className="p-0 md:p-12"
          />
        )}
      </div>
    </Modal>
  );
}

